import {
    HttpRequest,
    HttpResult,
    ReduxHttpRequest,
    AuthorizationErrorResult,
    TransportErrorResult,
    HttpRequestTypes,
    ReduxHttpRequestTypes,
    HttpRequestWithBody,
    HttpRequestWithBodyTypes,
    ReduxHttpRequestWithBody,
    ReduxHttpRequestWithBodyTypes,
    ErrorResponseResult
} from "rd-redux-http";
import { put, call } from "redux-saga/effects";

export interface SagaOperations<TParams, TResult, TError> {
    saga(params: TParams): IterableIterator<TResult>[];
    isSagaException(e: any): e is SagaHttpRequestError<TError>;
}

export interface SagaWithBodyOperations<TBody, TParams, TResult, TError> {
    saga(params: TParams, body: TBody): IterableIterator<TResult>[];
    isSagaException(e: any): e is SagaHttpRequestError<TError>;
}

export interface SagaHttpRequest<TParams, TResult, TError>
    extends HttpRequest<TParams, TResult, TError>,
        SagaOperations<TParams, TResult, TError> {
    types: HttpRequestTypes<TParams, TResult, TError> & SagaTypes<TResult, TError>;
}

export interface SagaHttpRequestWithBody<TBody, TParams, TResult, TError>
    extends HttpRequestWithBody<TBody, TParams, TResult, TError>,
        SagaWithBodyOperations<TBody, TParams, TResult, TError> {
    types: HttpRequestWithBodyTypes<TBody, TParams, TResult, TError> & SagaTypes<TResult, TError>;
}

export interface SagaReduxHttpRequest<TParams, TResult, TError>
    extends ReduxHttpRequest<TParams, TResult, TError>,
        SagaOperations<TParams, TResult, TError> {
    types: ReduxHttpRequestTypes<TParams, TResult, TError> & SagaTypes<TResult, TError>;
}

export interface SagaReduxHttpRequestWithBody<TBody, TParams, TResult, TError>
    extends ReduxHttpRequestWithBody<TBody, TParams, TResult, TError>,
        SagaWithBodyOperations<TBody, TParams, TResult, TError> {
    types: ReduxHttpRequestWithBodyTypes<TBody, TParams, TResult, TError> & SagaTypes<TResult, TError>;
}

export interface SagaTypes<TResult, TError> {}

export class SagaHttpRequestError<TError> extends Error {
    constructor(
        public requestInstanceId: string,
        public errorResult: AuthorizationErrorResult | TransportErrorResult | ErrorResponseResult<TError>
    ) {
        super(
            errorResult
                ? errorResult.errorType === "authorization"
                    ? `${errorResult.status}: ${errorResult.errorType}`
                    : errorResult.errorType === "response"
                        ? `${JSON.stringify(errorResult.error)}`
                        : `${errorResult.statusCode}: ${errorResult.error}`
                : "Network error"
        );
    }
}

export interface WithSaga {
    withSaga<TBody, TParams, TResult, TError = {}>(
        request: HttpRequestWithBody<TBody, TParams, TResult, TError>
    ): SagaReduxHttpRequestWithBody<TBody, TParams, TResult, TError>;

    withSaga<TBody, TParams, TResult, TError = {}>(
        request: ReduxHttpRequestWithBody<TBody, TParams, TResult, TError>
    ): SagaReduxHttpRequestWithBody<TBody, TParams, TResult, TError>;

    withSaga<TParams, TResult, TError = {}>(
        request: ReduxHttpRequest<TParams, TResult, TError>
    ): SagaReduxHttpRequest<TParams, TResult, TError>;

    withSaga<TParams, TResult, TError = {}>(
        request: HttpRequest<TParams, TResult, TError>
    ): SagaReduxHttpRequest<TParams, TResult, TError>;
}

let sagaRequestInstanceId = 0;

export const saga: WithSaga = {
    withSaga<TParams, TResult, TError = {}>(request: HttpRequest<TParams, TResult, TError>): any {
        const requestInstanceId = `${sagaRequestInstanceId++}`;

        (request as any).saga = function*(params: TParams, body?: any) {
            yield put(request.actions.running(params));

            const result: HttpResult<TResult, TError> = yield call(request, params, body);

            if (result.ok === true) {
                yield put(request.actions.ok(params, result));

                return result.result;
            } else {
                yield put(request.actions.error(params, result as any));

                throw new SagaHttpRequestError<TError>(requestInstanceId, result);
            }
        };

        (request as any).isSagaException = (e: any): e is SagaHttpRequestError<TError> => {
            return !!e && e.requestInstanceId === requestInstanceId;
        };

        return request;
    }
} as any;
